import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { TaskStatus } from '../../types';

const STATUS_LABELS: Record<TaskStatus, string> = {
  'pending': 'Not Started',
  'in-progress': 'In Progress',
  'review': 'Review',
  'completed': 'Completed',
  'blocked': 'Blocked',
  'on-hold': 'On Hold',
};

const ALL_STATUSES: TaskStatus[] = ['pending', 'in-progress', 'review', 'completed', 'blocked', 'on-hold'];

interface StatusPillProps {
  status: TaskStatus;
  onChange?: (status: TaskStatus) => void;
  readonly?: boolean;
  /** Lets parent rows raise z-index while the status menu is open (table stacking). */
  onOpenChange?: (open: boolean) => void;
}

type MenuPos = { top?: number; bottom?: number; left?: number; right?: number };

const StatusPill: React.FC<StatusPillProps> = ({ status, onChange, readonly, onOpenChange }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  useLayoutEffect(() => {
    if (!open || !ref.current) {
      setMenuPos(null);
      return;
    }
    const place = () => {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      const menuWidth = 180;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 220 && rect.top > spaceBelow;
      // Prefer right-align when the trigger sits near the viewport’s right edge
      // (gantt left-panel status column / task detail slide-over).
      const alignRight = rect.right > window.innerWidth - menuWidth - 12;
      const next: MenuPos = alignRight
        ? { right: Math.max(8, window.innerWidth - rect.right) }
        : { left: Math.max(8, rect.left) };
      if (openUp) next.bottom = window.innerHeight - rect.top + 4;
      else next.top = rect.bottom + 4;
      setMenuPos(next);
    };
    place();
    window.addEventListener('resize', place);
    // Capture scroll from gantt left/right panes and the task detail slide-over.
    document.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      document.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const editable = !readonly && !!onChange;

  const menu =
    open &&
    menuPos &&
    createPortal(
      <div
        ref={menuRef}
        className="pm-dropdown pm-status-menu pm-status-dropdown--portal"
        role="listbox"
        aria-label="Task status"
        style={menuPos}
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      >
        {ALL_STATUSES.map(s => (
          <button
            key={s}
            type="button"
            role="option"
            aria-selected={s === status}
            className="pm-dropdown-item"
            onMouseDown={e => {
              // Prefer mousedown so the choice commits before any parent drag/click handlers
              // (table row drag, gantt row select, detail overlay).
              e.preventDefault();
              e.stopPropagation();
              onChange?.(s);
              setOpen(false);
            }}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <span className="pm-status-pill" data-status={s}>
              {STATUS_LABELS[s]}
            </span>
          </button>
        ))}
      </div>,
      document.body,
    );

  return (
    <div
      ref={ref}
      className="pm-status-pill-host"
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
    >
      <div
        className={`pm-status-pill${editable ? '' : ' pm-status-pill--readonly'}`}
        data-status={status}
        onClick={() => {
          if (!editable) return;
          setOpen(prev => !prev);
        }}
      >
        {STATUS_LABELS[status]}
      </div>
      {menu}
    </div>
  );
};

export default StatusPill;
